import { debounce } from "lodash";
import Multiselect from "multiselect-react-dropdown";
import React, { useCallback, useEffect, useState } from "react";
import { Button, CardTitle, Col, Container, FormGroup, Label, Row } from "reactstrap";
import api from "../../../../../services/api";
import Loader from "../../../../loaders";
import { ComboBox } from "../../../../dropdowns";
import ReviewDialog from "./ReviewDialog";
import SweetAlert from "react-bootstrap-sweetalert";

export default function Editor(props)
{
    const [loading, setLoading] = useState(false);
    const [selectedBaseProject, setSelectedBaseProject] = useState(null);
    const [projectList, setProjectList] = useState([]);
    const [selectedMergedProjectList, setSelectedMergedProjectList] = useState([]);
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [mergingData, setMergingData] = useState(null);
    const [messageObj, setMessageObj] = useState(null);

    const getProjects = useCallback(debounce(filter => {
        if (!selectedBaseProject?.projectId)
        {
            setProjectList([]);
            return;
        }

        setLoadingProjects(true);
        api.get(`/project/search/false?excludedProjectId=${selectedBaseProject.projectId}&filter=${filter}`)
            .then(({ data }) => {
                setProjectList(data);
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => setLoadingProjects(false));
    }, 200), [selectedBaseProject]);

    useEffect(() => {
        getProjects("");
    }, [selectedBaseProject]);

    const onProjectsFilterChangeMultiselect = useCallback((value) => {
        getProjects(encodeURIComponent(value));
    }, [getProjects]);

    const onProjectsChangeMultiselect = useCallback((values) => {
        setSelectedMergedProjectList(values);
    }, []);
    
    const onProjectsRemove = ((newList) => {
        setSelectedMergedProjectList([...newList]);
        getProjects('');
    }, [getProjects]);

    const handleCheckMergingData = useCallback(() => {
        if (!selectedMergedProjectList?.length)
            return;

        setLoading(true);

        api.post("project/roster/merge/check", JSON.stringify(selectedMergedProjectList.map(p => p.projectId)))
            .then(response => {
                setMergingData(response.data);
            })
            .catch(error => {
                console.error({error});
            })
            .finally(() => setLoading(false))
        ;
    }, [selectedMergedProjectList]);

    const handleMerge = useCallback(() => {
        if (!selectedMergedProjectList?.length)
            return;

        setLoading(true);

        const dataToSend = {
            baseProjectId: selectedBaseProject.projectId,
            mergedProjectIds: selectedMergedProjectList.map(p => p.projectId),
        };

        api.post("project/roster/merge", JSON.stringify(dataToSend))
            .then(() => setMessageObj({message: "Project has been merged successfully."}))
            .catch(error => {
                console.error({error});
                setMessageObj({message: "Fail to merge the projects. Please contact your administrator."});
            })
            .finally(() => setLoading(false))
        ;
    }, [selectedBaseProject, selectedMergedProjectList]);

    return (
        <section className="main">
            {
                (loading) && <Loader />
            }
            <Container fluid>
                <Row>
                    <Col lg={12}>
                        <CardTitle>
                            <h4 className="text-center">Roster - Merge Project</h4>
                        </CardTitle>
                    </Col>
                </Row>
                <Row className="text-justify">
                    <Col lg={3} md={2} className="hidden-sm">
                    </Col>
                    <Col lg={6} md={8} sm={12}>
                        <Row>
                            <Col sm={12} md={6}>
                                <FormGroup>
                                    <Label for="projectName">
                                        Base Project
                                        <span className="text-danger">*</span>
                                    </Label>
                                    <ComboBox
                                        endpoint="/project/search"
                                        isLookup={false}
                                        idField="projectId"
                                        valueField="projectName"
                                        selectedItem={selectedBaseProject}
                                        onChange={setSelectedBaseProject}
                                        minLength={0}
                                    />
                                </FormGroup>
                            </Col>
                            <Col sm={12} md={6}>
                                <FormGroup>
                                    <Label for="projectName">
                                        Merged Projects
                                        <span className="text-danger">*</span>
                                    </Label>
                                    <Multiselect
                                        className="slim-search"
                                        options={projectList}
                                        loading={loadingProjects}
                                        displayValue="projectName"
                                        placeholder="Type to search"
                                        selectedValues={selectedMergedProjectList}
                                        onSearch={onProjectsFilterChangeMultiselect}
                                        onSelect={(selected) => {
                                            onProjectsChangeMultiselect(selected);
                                        }}
                                        onRemove={onProjectsRemove}
                                        showCheckbox={true}
                                        showArrow={true}
                                        closeOnSelect={true}
                                        disable={!selectedBaseProject?.projectId}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row>
                    <Col lg={3} md={2} className="hidden-sm">
                    </Col>
                    <Col lg={6} md={8} sm={12} className="d-flex flex-wrap justify-content-end">
                        <Button
                            color="default"
                            onClick={() => {
                                handleCheckMergingData();
                            }}
                            type="button"
                            disabled={!selectedMergedProjectList?.length}
                        >
                            Check Merged Data
                        </Button>
                    </Col>
                </Row>
            </Container>
        {
            mergingData ?
            <ReviewDialog
                mergingData={mergingData}
                onCancel={() => setMergingData(null)}
                onSubmit={() => {
                    setMergingData(null);
                    handleMerge();
                }}
            />
            :
            null
        }
        {
            messageObj ?
            <SweetAlert
                title={<h4 className="mb-0 text-center">Project Merging</h4>}
                onConfirm={() => setMessageObj(null)}
                type="info"
            >
                <div className="mb-2 text-center">{messageObj.message}</div>
            </SweetAlert>
            :
            null
        }
        </section>
    );
}